import { api } from './client';

// Estados visíveis ao usuário — vocabulário em português congelado no plano.
// SEM_DADOS é distinto de INDISPONIVEL: significa "não temos amostra recente
// para opinar" (ex.: o synthetic não rodou). Não pintar de vermelho.
export type StatusAlvo = 'ATIVO' | 'DEGRADADO' | 'INDISPONIVEL' | 'SEM_DADOS';
export type Severidade = 'MENOR' | 'MAIOR' | 'CRITICO';
export type EstadoIncidente = 'ABERTO' | 'FECHADO' | 'TODOS';

// O autenticador serializa cada campo com @JsonProperty explícito e o
// backend repassa o mesmo shape via proxy HMAC. Types abaixo refletem a
// representação JSON real que chega ao frontend.

export interface AlvoStatus {
  alvo: string;
  status: StatusAlvo;
  latencia_p95_1h_ms: number | null;
  taxa_erro_1h: number | null;
  uptime_24h: number | null;
  uptime_7d: number | null;
  incidente_ativo_id: number | null;
  incidente_ativo_severidade: Severidade | null;
  ultima_amostra_em: string | null;
  sincronizado_em: string;
}

export interface MonitoramentoStatus {
  gerado_em: string;
  alvos: AlvoStatus[];
}

export interface BucketSerie {
  inicio_bucket: string;
  status_dominante: StatusAlvo;
  total: number;
  count_up: number;
  count_degraded: number;
  count_down: number;
  p50_ms: number | null;
  p95_ms: number | null;
  p99_ms: number | null;
}

export interface SerieAlvo {
  alvo: string;
  bucket: string;
  inicio_janela: string;
  fim_janela: string;
  buckets: BucketSerie[];
}

export interface AmostraResumo {
  observado_em: string;
  status: StatusAlvo;
  latencia_ms: number | null;
  codigo_erro: string | null;
}

export interface Incidente {
  id: number;
  alvo: string;
  severidade: Severidade;
  aberto_em: string;
  fechado_em: string | null;
  razao_abertura: string | null;
  falhas_consecutivas: number | null;
  ultimo_erro: string | null;
  ack_por: string | null;
  ack_em: string | null;
  amostras: AmostraResumo[];
}

export interface ListaIncidentes {
  estado: EstadoIncidente | string;
  limite: number;
  offset: number;
  incidentes: Incidente[];
}

export async function fetchMonitoramentoStatus(): Promise<MonitoramentoStatus> {
  const { data } = await api.get<MonitoramentoStatus>('/admin/monitoramento/status');
  return data;
}

export async function fetchSerieAlvo(
  alvo: string,
  params: { bucket?: string; janela?: string } = {}
): Promise<SerieAlvo> {
  const { data } = await api.get<SerieAlvo>(
    `/admin/monitoramento/alvos/${encodeURIComponent(alvo)}/serie`,
    { params: { bucket: params.bucket ?? 'hora', janela: params.janela ?? '7d' } }
  );
  return data;
}

export async function fetchSerieAgregada(
  alvos: string[],
  janela: '1h' | '6h' | '24h' | '7d' = '24h'
): Promise<SerieAlvo> {
  const { data } = await api.get<SerieAlvo>('/admin/monitoramento/serie/agregada', {
    params: { alvos, janela },
    // axios serializa array como `alvos=a&alvos=b` (formato esperado pelo
    // Spring @RequestParam List<String>) com paramsSerializer abaixo.
    paramsSerializer: (params) => {
      const usp = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => {
        if (Array.isArray(v)) v.forEach((item) => usp.append(k, String(item)));
        else if (v !== undefined && v !== null) usp.append(k, String(v));
      });
      return usp.toString();
    },
  });
  return data;
}

export async function fetchIncidentes(params: {
  estado?: EstadoIncidente;
  limite?: number;
  offset?: number;
}): Promise<ListaIncidentes> {
  const { data } = await api.get<ListaIncidentes>('/admin/monitoramento/incidentes', {
    params: {
      estado: params.estado ?? 'ABERTO',
      limite: params.limite ?? 50,
      offset: params.offset ?? 0,
    },
  });
  return data;
}

export async function fetchIncidente(id: number): Promise<Incidente> {
  const { data } = await api.get<Incidente>(`/admin/monitoramento/incidentes/${id}`);
  return data;
}

export async function registrarAckIncidente(id: number): Promise<void> {
  await api.post(`/admin/monitoramento/incidentes/${id}/ack`);
}

// ---------------------------------------------------------------------------
// Jobs (visão micro — aba Jobs do admin)
// ---------------------------------------------------------------------------

export type TipoJob =
  | 'carga_processo'
  | 'carga_tramitacao'
  | 'peticao_tramitacao'
  | 'peticao_status_portal';

export interface MonitoramentoJob {
  tipo: TipoJob;
  id: number;
  chave: string;
  alvo: string | null;
  status: string;
  tentativas: number | null;
  erro: string | null;
  criado_em: string | null;
  iniciado_em: string | null;
  finalizado_em: string | null;
  proximo_retry_em: string | null;
  // CPF do usuário que originou o job (cru, sem máscara — UI formata).
  cpf: string | null;
  // Request ID do MDC HTTP. Usar os 8 primeiros chars como link para o
  // Observability filtrado por esse request_id.
  request_id: string | null;
}

export interface ListaJobs {
  tipo: string;
  status: string;
  page: number;
  page_size: number;
  total: number;
  jobs: MonitoramentoJob[];
}

export async function fetchJobs(params: {
  tipo?: TipoJob;
  status?: string;
  alvo?: string;
  page?: number;
  pageSize?: number;
} = {}): Promise<ListaJobs> {
  const { data } = await api.get<ListaJobs>('/admin/monitoramento/jobs', {
    params: {
      ...(params.tipo ? { tipo: params.tipo } : {}),
      ...(params.status ? { status: params.status } : {}),
      ...(params.alvo ? { alvo: params.alvo } : {}),
      page: params.page ?? 1,
      page_size: params.pageSize ?? 20,
    },
  });
  return data;
}

// ---------------------------------------------------------------------------
// Drill-in do alvo (Sheet de detalhe)
// ---------------------------------------------------------------------------

export interface OperacaoAlvo {
  operation: string;
  total: number;
  sucesso: number;
  falha: number;
  // null quando não há amostras na janela (denominador zero) — distingue
  // "100% sucesso" de "ninguém chamou".
  percentualSucesso: number | null;
  p95LatenciaMs: number | null;
  ultimoErroCodigo: string | null;
  ultimaAmostraEm: string | null;
}

export interface ListaOperacoes {
  alvo: string;
  janela: string;
  lista: OperacaoAlvo[];
}

export interface ListaIncidentesAlvo {
  alvo: string;
  limite: number;
  lista: Incidente[];
}

export async function fetchOperacoesDoAlvo(alvo: string, janela: '1h' | '6h' | '24h' | '7d' = '1h'): Promise<ListaOperacoes> {
  const { data } = await api.get<ListaOperacoes>(
    `/admin/monitoramento/alvos/${encodeURIComponent(alvo)}/operacoes`,
    { params: { janela } },
  );
  return data;
}

export async function fetchIncidentesDoAlvo(alvo: string, limite = 20): Promise<ListaIncidentesAlvo> {
  const { data } = await api.get<ListaIncidentesAlvo>(
    `/admin/monitoramento/alvos/${encodeURIComponent(alvo)}/incidentes`,
    { params: { limite } },
  );
  return data;
}

// ---------------------------------------------------------------------------
// Amostras cruas do bucket (drill da uptime bar)
// ---------------------------------------------------------------------------

export type TipoAmostra = 'DERIVED' | 'SYNTHETIC';

export interface AmostraDetalhe {
  observado_em: string;
  tipo: TipoAmostra;
  status: StatusAlvo;
  latencia_ms: number | null;
  codigo_erro: string | null;
  // JSON cru — costuma carregar `operation`, `reqId`, `outcome` (DERIVED)
  // ou `code`/`erro` (SYNTHETIC).
  detalhe: string | null;
}

export interface ListaAmostrasAlvo {
  alvo: string;
  inicio: string;
  fim: string;
  limite: number;
  lista: AmostraDetalhe[];
}

export async function fetchAmostrasDoAlvo(
  alvo: string,
  inicio: string,
  fim: string,
  limite = 200,
): Promise<ListaAmostrasAlvo> {
  const { data } = await api.get<ListaAmostrasAlvo>(
    `/admin/monitoramento/alvos/${encodeURIComponent(alvo)}/amostras`,
    { params: { inicio, fim, limite } },
  );
  return data;
}

export interface ResumoJobs {
  sucesso24h: number;
  falha24h: number;
  sucesso1h: number;
  falha1h: number;
  pendentes: number;
  emExecucao: number;
  // null quando não há jobs finalizados na janela (denominador zero) — distingue
  // "100% sucesso" de "ninguém terminou ainda". Mesma regra do SLI derivado.
  percentualSucesso24h: number | null;
  percentualSucesso1h: number | null;
}

export async function fetchResumoJobs(): Promise<ResumoJobs> {
  const { data } = await api.get<ResumoJobs>('/admin/monitoramento/jobs/resumo');
  return data;
}

// ---------------------------------------------------------------------------
// Config do detector de incidentes (alimenta o glossário)
// ---------------------------------------------------------------------------

// `taxa_erro` é null para CRITICO; `indisponivel_min` é null para MENOR/MAIOR.
// O autenticador é a fonte da verdade — qualquer ajuste de threshold lá vira
// tooltip atualizado aqui sem mudança de código.
export interface LimiarSeveridade {
  consecutivas: number;
  taxa_erro: number | null;
  indisponivel_min: number | null;
}

export interface SeveridadesConfig {
  MENOR: LimiarSeveridade;
  MAIOR: LimiarSeveridade;
  CRITICO: LimiarSeveridade;
}

export interface RegrasStreakConfig {
  conta_apenas_indisponivel: boolean;
  degradado_neutro_no_streak: boolean;
  severidade_max_ignora_volume_minimo: boolean;
}

export interface JobsConfig {
  janela_min: number;
  intervalo_s: number;
  taxa_falha_threshold: number;
  min_falhas: number;
  min_sucesso_para_fechar: number;
  severidade_fixa: string;
}

export interface MonitoramentoConfig {
  janela_min: number;
  intervalo_detector_s: number;
  min_amostras_para_taxa: number;
  fechamento_consecutivas_ativo: number;
  severidades: SeveridadesConfig;
  regras_streak: RegrasStreakConfig;
  jobs: JobsConfig;
}

export async function fetchMonitoramentoConfig(): Promise<MonitoramentoConfig> {
  const { data } = await api.get<MonitoramentoConfig>('/admin/monitoramento/config');
  return data;
}
